import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {deprecatedTrackEvent} from 'analytics/Analytics';
import {INSIGHTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {BLOCK_EDITOR_TOP_ROW_HEIGHT, PANE_MARGIN} from 'insights/InsightsConstants';
import {schemaQualifiedTableNameToSchemaAndCompleteTableNames} from 'insights/warehouse/WarehouseUtils';
import {ellipsisText} from 'styles/Utilities';
import {TooltipPlacement} from 'ui-library/bs/Tooltip';
import ClipboardText from 'ui-library/ClipboardText';
import {GRAY, GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import LoadingPaneContents from 'ui-library/pane/LoadingPaneContents';
import Pane from 'ui-library/pane/Pane';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import ResultsPager from 'ui-library/ResultsPager';
import {InputChangeEvent} from 'util/Types';

import OpenEntityDiagramButton from './OpenEntityDiagramButton';
import trackOnCopy from './trackOnCopy';

const PANE_TITLE = 'Schema browser';

interface Actions {
  openTable: (fullTableName: string) => void;
  setQuery: (query: string) => void;
}

function useTrackOnSearch(query: string, numResults: number): void {
  const debouncedValue = useMemo(() => ({query, numResults}), [query, numResults]);
  const {query: debouncedQuery, numResults: debouncedNumResults} = useDebounce(debouncedValue, 1000);
  useEffect(() => {
    if (debouncedQuery.length) {
      deprecatedTrackEvent(INSIGHTS_ANALYTICS_ACTIONS.SEARCHED_IN_SCHEMA_BROWSER, {
        query_length: debouncedQuery.length,
        num_results: debouncedNumResults,
      });
    }
  }, [debouncedQuery, debouncedNumResults]);
}

export function WarehouseTableRow({
  actions,
  fullTableName,
}: {
  actions: Pick<Actions, 'openTable'>;
  fullTableName: string;
}): JSX.Element {
  const [isHovered, hoverProps] = useHoverState();
  const [schemaName, completeTableName] =
    schemaQualifiedTableNameToSchemaAndCompleteTableNames(fullTableName);

  return (
    <div
      className={classNames(css(styles.warehouseTableRow), 'u-vertical-center-items')}
      onClick={() => {
        actions.openTable(fullTableName);
      }}
      {...hoverProps}
    >
      <ClipboardText
        clipboardData={fullTableName}
        onClick={trackOnCopy}
        copyIconAlwaysVisible={isHovered}
        disableTooltip={!isHovered}
        tooltipPlacement={FloatingPlacement.TOP}
        className={classNames('u-vertical-center-items', css(styles.clipboardTextWrapper))}
      >
        <BenchlingIcon
          className={classNames(css(styles.warehouseTableIcon), 'u-mini-spacing-right')}
          icon="warehouse-table"
        />
        <div className={css(styles.ellipsisText)}>{fullTableName}</div>
        {isHovered && (
          <OpenEntityDiagramButton
            schemaName={schemaName}
            tableName={completeTableName}
            useHoverIcon={true}
            tooltipPlacement={TooltipPlacement.TOP}
          />
        )}
      </ClipboardText>
    </div>
  );
}

const ROW_LIMIT = 100;

export interface WarehouseTableListingProps {
  actions: Actions;
  additionalActionButtons?: JSX.Element;
  matchedSortedFullTableNames: Array<string>;
  query: string;
}

export default function WarehouseTableListing({
  actions,
  additionalActionButtons,
  matchedSortedFullTableNames,
  query,
}: WarehouseTableListingProps): JSX.Element {
  useTrackOnSearch(query, matchedSortedFullTableNames.length);
  const [rowsOffset, setRowsOffset] = useState(0);
  const rowListWrapperRef = useRef<HTMLDivElement>(null);

  function resetScroll(): void {
    if (rowListWrapperRef.current) {
      rowListWrapperRef.current.scrollTop = 0;
    }
  }

  function handleChangeQuery(newQuery: string): void {
    actions.setQuery(newQuery);
    // Go back to top of first page.
    setRowsOffset(0);
    resetScroll();
  }

  const tablesToShow = matchedSortedFullTableNames.slice(rowsOffset, rowsOffset + ROW_LIMIT);

  return (
    <Pane
      title={PANE_TITLE}
      fitContent={true}
      toolbar={
        <div className="u-vertical-center-items">
          <PlainSearchInput
            className={css(styles.searchInput)}
            clearType={ClearType.X}
            onChange={(e: InputChangeEvent) => {
              handleChangeQuery(e.target.value);
            }}
            onEscape={() => {
              handleChangeQuery('');
            }}
            placeholder="Search"
            value={query}
          />
          {additionalActionButtons && <span className="u-mini-spacing-left">{additionalActionButtons}</span>}
        </div>
      }
    >
      <div className={classNames(css(styles.warehouseTableListing))}>
        <div ref={rowListWrapperRef} className={classNames(css(styles.rowListWrapper), 'scrollbar-override')}>
          {tablesToShow.map((fullTableName) => (
            <WarehouseTableRow key={fullTableName} actions={actions} fullTableName={fullTableName} />
          ))}
        </div>
        {matchedSortedFullTableNames.length > ROW_LIMIT && (
          <div className={css(styles.pagerWrapper)}>
            <ResultsPager
              setOffset={(newRowsOffset) => {
                setRowsOffset(newRowsOffset);
                resetScroll();
              }}
              resultDescription={null}
              isLoading={false}
              pagination={{limit: ROW_LIMIT, offset: rowsOffset, total: matchedSortedFullTableNames.length}}
            />
          </div>
        )}
      </div>
    </Pane>
  );
}

export function LoadingWarehouseTableListing({
  additionalActionButtons,
}: Pick<WarehouseTableListingProps, 'additionalActionButtons'>): JSX.Element {
  return (
    <Pane title={PANE_TITLE} toolbar={additionalActionButtons} fitContent={true}>
      <LoadingPaneContents />
    </Pane>
  );
}

const styles = StyleSheet.create({
  clipboardTextWrapper: {
    display: 'flex',
    width: '100%',
  },
  searchInput: {
    width: 241,
  },
  warehouseTableIcon: {
    color: GRAY,
    flexShrink: 0,
  },
  warehouseTableListing: {
    maxHeight: BLOCK_EDITOR_TOP_ROW_HEIGHT,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  pagerWrapper: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    paddingLeft: PANE_MARGIN,
    paddingRight: 3,
  },
  rowListWrapper: {
    overflowY: 'auto',
  },
  warehouseTableRow: {
    cursor: 'pointer',
    fontSize: 13,
    paddingTop: 3,
    paddingRight: 3,
    paddingBottom: 3,
    paddingLeft: PANE_MARGIN,
    width: '100%',
    height: 26,
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
    },
  },
  ellipsisText,
});
